import { action, computed, makeAutoObservable } from "mobx";
import { DateRange } from "react-day-picker";

import { getEntityVariantKey } from "../ChooseEntityDialog/ChooseEntityDialogContent/utils";

import FilterStore from "./filter-store";
import { SortField, SortState } from "./types";
import type { UnifiedRegistryOptions } from "./unified-registry-context";

import { EntityType } from "@/queries";
import type { Entity } from "@/queries";

interface FilterSchema {
  search_query?: string;
  date_range?: DateRange;
  entity_types?: EntityType[];
  sort_state?: SortState;
  selected_user_id?: number;
  tags?: string[];
}

class UnifiedRegistryStore {
  private _debouncedSearchQuery = "";
  private _debounceTimeout: NodeJS.Timeout | null = null;
  private _selectedEntities: Entity[] = [];
  private options: UnifiedRegistryOptions = {};

  private _filters = new FilterStore<FilterSchema>();

  constructor(options: UnifiedRegistryOptions) {
    makeAutoObservable(this);
    this.options = options;
  }

  @action
  setOptions(options: UnifiedRegistryOptions) {
    this.options = options;
  }

  get selectionDisabled() {
    return this.options.disableSelection ?? false;
  }

  get createInNewTab() {
    return this.options.createInNewTab ?? false;
  }

  get modal() {
    return this.options.modal ?? false;
  }

  get multiSelectMode() {
    return this.options.multiSelectMode ?? false;
  }

  get allowDuplicateVariants() {
    return this.options.allowDuplicateVariants ?? false;
  }

  get filters() {
    return this._filters;
  }

  @action
  setSearchQuery(searchQuery: string) {
    this._filters.setFilter("search_query", searchQuery);

    if (this._debounceTimeout) {
      clearTimeout(this._debounceTimeout);
    }

    this._debounceTimeout = setTimeout(
      action(() => {
        this._debouncedSearchQuery = searchQuery;
      }),
      300,
    );
  }

  get whitelist() {
    return this.options.whitelist;
  }

  @computed
  isDisabled(entity: Entity) {
    // Check if disabled is a function
    if (typeof this.options.disabled === "function") {
      if (this.options.disabled(entity)) {
        return true;
      }
    }
    // Check if disabled is an array
    else if (Array.isArray(this.options.disabled)) {
      if (
        this.options.disabled.some(
          disabledEntity => disabledEntity.id === entity.id && disabledEntity.type === entity.type,
        )
      ) {
        return true;
      }
    }

    if (!this.whitelist || this.whitelist.length === 0) {
      return false;
    }
    // Folders should always be accessible for navigation
    if (entity.type === EntityType.FOLDER) {
      return false;
    }
    return !this.whitelist.includes(entity.type);
  }

  get searchQuery() {
    return this._filters.getFilter("search_query") ?? "";
  }

  @computed
  get debouncedSearchQuery() {
    return this._debouncedSearchQuery;
  }

  @computed
  get isPendingDebounce() {
    return this.searchQuery !== this._debouncedSearchQuery;
  }

  setDateRange(dateRange?: DateRange) {
    this._filters.setFilter("date_range", dateRange);
  }

  get dateRange() {
    return this._filters.getFilter("date_range");
  }

  setEntityTypes(entityTypes: EntityType[]) {
    this._filters.setFilter("entity_types", entityTypes);
  }

  get entityTypes() {
    return this._filters.getFilter("entity_types");
  }

  setSelectedUserId(userId: number | null) {
    this._filters.setFilter("selected_user_id", userId ?? undefined);
  }

  get selectedUserId(): number | null {
    return this._filters.getFilter("selected_user_id") ?? null;
  }

  setTags(tags: string[]) {
    this._filters.setFilter("tags", tags.length > 0 ? tags : undefined);
  }

  get tags(): string[] {
    return this._filters.getFilter("tags") ?? [];
  }

  get hasActiveFilters() {
    return Boolean(
      (this.searchQuery?.length ?? 0) > 0 ||
        (this.entityTypes?.length ?? 0) > 0 ||
        this.dateRange ||
        this.selectedUserId ||
        (this.tags?.length ?? 0) > 0,
    );
  }

  get isFlattened() {
    return this.hasActiveFilters;
  }

  handleSort(field: SortField) {
    const currentSortState = this.sortState;

    if (currentSortState?.field === field) {
      this.setSortState({
        field,
        direction: currentSortState.direction === "asc" ? "desc" : "asc",
      });
    } else {
      this.setSortState({
        field,
        direction: field === "modified" || field === "created" ? "desc" : "asc",
      });
    }
  }

  setSortState(sortState: SortState) {
    this._filters.setFilter("sort_state", sortState);
  }

  get sortState() {
    return this._filters.getFilter("sort_state");
  }

  get selectedEntities() {
    return this._selectedEntities;
  }

  isSelected(entity: Entity): boolean {
    if (this.selectionDisabled) {
      return false;
    }
    const entityKey = getEntityVariantKey(entity, this.allowDuplicateVariants);
    return this._selectedEntities.some(
      e => getEntityVariantKey(e, this.allowDuplicateVariants) === entityKey,
    );
  }

  @action
  selectEntity(entity: Entity) {
    if (this.selectionDisabled) {
      return;
    }
    if (this.isSelected(entity)) {
      return;
    }
    this._selectedEntities.push(entity);
  }

  @action selectEntities(entities: Entity[]) {
    if (this.selectionDisabled) {
      return;
    }
    this._selectedEntities = entities;
  }

  @action
  deselectEntity(entity: Entity) {
    if (this.selectionDisabled) {
      return;
    }
    const entityKey = getEntityVariantKey(entity, this.allowDuplicateVariants);
    const index = this._selectedEntities.findIndex(
      e => getEntityVariantKey(e, this.allowDuplicateVariants) === entityKey,
    );
    if (index === -1) {
      return;
    }
    this._selectedEntities.splice(index, 1);
  }

  @action
  toggleEntitySelection(entity: Entity) {
    if (this.selectionDisabled) {
      return;
    }
    if (this.isSelected(entity)) {
      this.deselectEntity(entity);
    } else {
      this.selectEntity(entity);
    }
  }

  @action
  clearSelection() {
    if (this.selectionDisabled) {
      return;
    }
    this._selectedEntities = [];
  }

  @computed
  get selectedCount() {
    return this._selectedEntities.length;
  }

  @action
  clearAllFilters() {
    this._filters.clear();
    if (this._debounceTimeout) {
      clearTimeout(this._debounceTimeout);
    }
    this._debouncedSearchQuery = "";
  }

  @action
  resetOnNavigation() {
    this.clearAllFilters();
    this.clearSelection();
  }
}

export default UnifiedRegistryStore;
